import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { playerApi } from "../api/playerApi";
import { readStoredAuth } from "../auth/authStorage";
import { resolveUrl } from "../utils/urls";
import { isAegisEmbedUrl, resolveAegisPlayerUrl } from "./aegisPlayer";

const PodcastPlayerContext = createContext(null);
const QUEUE_STORAGE_KEY = "bs_podcast_player_queue_v1";
const GLOBAL_VISIBILITY_STORAGE_KEY = "bs_podcast_player_global_visibility_v2";
const AEGIS_REFRESH_SKEW_SEC = 75;
const AEGIS_MIN_REFRESH_DELAY_SEC = 15;

function toSafeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeTrack(input = {}) {
  const raw = input && typeof input === "object" ? input : {};
  const assetId = String(
    raw.assetId ||
      raw.asset_id ||
      raw.aegisAssetId ||
      raw.aegis_asset_id ||
      ""
  ).trim();
  const projectId = String(
    raw.projectId ||
      raw.project_id ||
      raw.aegisProjectId ||
      raw.aegis_project_id ||
      ""
  ).trim();
  const knownAudioUrl = resolveUrl(
    raw.audioUrl ||
    raw.manifestUrl ||
    raw.manifest_url ||
    raw.streamManifestUrl ||
    raw.stream_manifest_url ||
    raw.stream?.manifestUrl ||
    raw.stream?.manifest_url ||
    ""
  );
  const explicitAegisUrl = resolveUrl(resolveAegisPlayerUrl(raw));
  const resolvedUrl = resolveUrl(
    explicitAegisUrl ||
    knownAudioUrl ||
    raw.playbackUrl ||
    raw.streamUrl ||
    raw.mediaUrl ||
    raw.fileUrl ||
    ""
  );
  const embedUrl = isAegisEmbedUrl(explicitAegisUrl || resolvedUrl) ? (explicitAegisUrl || resolvedUrl) : "";
  const audioUrl = embedUrl ? "" : resolvedUrl;
  const isAegis = Boolean(
    raw.isAegis ||
      raw.isAegisEmbed ||
      raw.aegisPlayerUrl ||
      raw.aegisEmbedUrl ||
      raw.aegisPodcastId ||
      raw.aegis_podcast_id ||
      assetId ||
      projectId ||
      embedUrl
  );
  const streamExpiresAt = String(raw.streamExpiresAt || raw.expiresAt || raw.expires_at || "").trim();
  const refreshAfterSec = toSafeNumber(raw.refreshAfterSec || raw.refresh_after_sec, 0);

  return {
    episodeId: raw.episodeId ?? raw.id ?? null,
    podcastId: raw.podcastId ?? null,
    podcastSlug: String(raw.podcastSlug || ""),
    title: String(raw.title || "Эпизод"),
    podcastTitle: String(raw.podcastTitle || raw.author || "Подкаст"),
    coverImageUrl: resolveUrl(raw.coverImageUrl || raw.imageUrl || raw.coverUrl || raw.posterUrl || raw.poster_url || ""),
    durationSeconds: toSafeNumber(raw.durationSeconds || raw.duration_seconds, 0),
    audioUrl,
    embedUrl,
    assetId,
    projectId,
    sessionId: String(raw.sessionId || raw.session_id || "").trim(),
    playbackToken: String(raw.playbackToken || raw.playback_token || raw.token || "").trim(),
    dashManifestUrl: resolveUrl(raw.dashManifestUrl || raw.dash_manifest_url || ""),
    isAegis,
    streamExpiresAt,
    refreshAfterSec: refreshAfterSec > 0 ? refreshAfterSec : 0,
  };
}

function canResolveTrack(input = {}) {
  const track = normalizeTrack(input);
  return Boolean(
    track.audioUrl ||
    (track.episodeId !== null && track.episodeId !== undefined) ||
    track.assetId
  );
}

function currentPlaybackUser() {
  try {
    const stored = readStoredAuth();
    const me = stored?.me && typeof stored.me === "object" ? stored.me : {};
    return {
      userId: me.id ?? me.userId ?? me.uuid ?? undefined,
      userEmail: me.email || me.userEmail || undefined,
      userName: me.name || me.fullName || me.displayName || undefined,
    };
  } catch {
    return {};
  }
}

function trackKey(input = {}) {
  const track = normalizeTrack(input);
  if (track.episodeId !== null && track.episodeId !== undefined) return `ep:${track.episodeId}`;
  if (track.assetId) return `asset:${track.assetId}`;
  if (track.audioUrl) return `url:${track.audioUrl}`;
  return `raw:${track.title}:${track.podcastTitle}`;
}

function dedupeQueue(items = []) {
  const out = [];
  const seen = new Set();
  for (const item of items) {
    const normalized = normalizeTrack(item);
    if (!canResolveTrack(normalized)) continue;
    const key = trackKey(normalized);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
  }
  return out;
}

function normalizeEpisodeId(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function normalizeEpisodeIdentity(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  const byPrefix = raw.match(/^ep[_-]?(\d+)$/i);
  if (byPrefix?.[1]) return byPrefix[1];
  const digitsOnly = raw.match(/^(\d+)$/);
  if (digitsOnly?.[1]) return digitsOnly[1];
  return raw;
}

function parseTimestampToMs(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const ts = new Date(raw).getTime();
  return Number.isFinite(ts) ? ts : null;
}

function toSafeLocale() {
  if (typeof navigator === "undefined") return "";
  return String(navigator.language || "").trim();
}

function extractQueueEpisodeIds(items = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    const normalized = normalizeTrack(item);
    const id = normalizeEpisodeId(normalized.episodeId);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

function buildBootstrapQueueEpisodeIds(episodeId, queueItems = []) {
  const requestedRaw = normalizeEpisodeId(episodeId);
  const requested = normalizeEpisodeIdentity(requestedRaw);
  const ids = extractQueueEpisodeIds(queueItems);
  if (!requestedRaw) return ids;

  const directIndex = ids.findIndex((id) => normalizeEpisodeId(id) === requestedRaw);
  if (directIndex > 0) {
    const next = [...ids];
    const [hit] = next.splice(directIndex, 1);
    next.unshift(hit);
    return next;
  }

  if (directIndex === 0) return ids;

  const looseIndex = ids.findIndex((id) => normalizeEpisodeIdentity(id) === requested);
  if (looseIndex > 0) {
    const next = [...ids];
    const [hit] = next.splice(looseIndex, 1);
    next.unshift(hit);
    return next;
  }

  if (looseIndex === 0) return ids;

  return [requestedRaw, ...ids];
}

function normalizeAegisStream(payload = {}) {
  const raw = payload && typeof payload === "object" ? payload : {};
  const nestedHls = raw.hls && typeof raw.hls === "object" ? raw.hls : {};
  const nestedManifest = raw.manifest && typeof raw.manifest === "object" ? raw.manifest : {};
  const manifestUrl = resolveUrl(
    raw.manifest_url ||
    raw.manifestUrl ||
    raw.url ||
    raw.hlsManifestUrl ||
    raw.hls_manifest_url ||
    nestedHls.manifest_url ||
    nestedHls.manifestUrl ||
    nestedHls.url ||
    nestedManifest.manifest_url ||
    nestedManifest.manifestUrl ||
    nestedManifest.url ||
    ""
  );
  if (!manifestUrl || isAegisEmbedUrl(manifestUrl)) return null;
  const expiresAt = String(raw.expires_at || raw.expiresAt || "").trim();
  const refreshAfterSec = toSafeNumber(raw.refresh_after_sec || raw.refreshAfterSec, 0);
  return {
    manifestUrl,
    dashManifestUrl: resolveUrl(raw.dash_manifest_url || raw.dashManifestUrl || ""),
    expiresAt,
    expiresAtMs: parseTimestampToMs(expiresAt),
    refreshAfterSec: refreshAfterSec > 0 ? refreshAfterSec : 0,
    sessionId: String(raw.session_id || raw.sessionId || "").trim(),
    playbackToken: String(raw.playback_token || raw.playbackToken || raw.token || "").trim(),
    assetId: String(raw.asset_id || raw.assetId || "").trim(),
    projectId: String(raw.project_id || raw.projectId || "").trim(),
    title: String(raw.title || "").trim(),
    posterUrl: resolveUrl(raw.poster_url || raw.posterUrl || ""),
    durationSeconds: toSafeNumber(raw.duration_seconds || raw.durationSeconds, 0),
  };
}

function normalizeOpenPlaybackPayload(payload = {}) {
  const raw = payload && typeof payload === "object" ? payload : {};
  const stream = normalizeAegisStream(raw);
  return {
    episodeId: raw.episode_id ?? raw.episodeId ?? null,
    assetId: String(raw.asset_id || raw.assetId || stream?.assetId || "").trim(),
    projectId: String(raw.project_id || raw.projectId || stream?.projectId || "").trim(),
    title: String(raw.title || stream?.title || "").trim(),
    posterUrl: resolveUrl(raw.poster_url || raw.posterUrl || stream?.posterUrl || ""),
    durationSeconds: toSafeNumber(raw.duration_seconds || raw.durationSeconds || stream?.durationSeconds, 0),
    stream,
  };
}

function normalizeBootstrapPayload(payload = {}) {
  const raw = payload && typeof payload === "object" ? payload : {};
  const current = raw.current && typeof raw.current === "object" ? raw.current : raw;
  const stream =
    normalizeAegisStream(current.stream || {}) ||
    normalizeAegisStream(raw.stream || {}) ||
    normalizeAegisStream(current.player || {}) ||
    normalizeAegisStream(raw.player || {}) ||
    normalizeAegisStream(current) ||
    normalizeAegisStream(raw);
  return {
    episodeId: current.episode_id ?? current.episodeId ?? raw.episode_id ?? raw.episodeId ?? null,
    title: String(current.title || "").trim(),
    author: String(current.author || "").trim(),
    posterUrl: resolveUrl(current.poster_url || current.posterUrl || ""),
    durationSeconds: toSafeNumber(current.duration_sec || current.durationSeconds, 0),
    stream,
  };
}

function clampIndex(index, size) {
  const n = Number(index);
  if (!Number.isFinite(n)) return -1;
  if (size <= 0) return -1;
  return Math.max(0, Math.min(size - 1, Math.floor(n)));
}

function readStoredQueue() {
  if (typeof window === "undefined") return { queue: [], index: -1 };
  try {
    const raw = window.localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) return { queue: [], index: -1 };
    const parsed = JSON.parse(raw);
    const queue = dedupeQueue(Array.isArray(parsed?.queue) ? parsed.queue : []);
    const index = clampIndex(parsed?.index, queue.length);
    return { queue, index };
  } catch {
    return { queue: [], index: -1 };
  }
}

function writeStoredQueue(queue, index) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      QUEUE_STORAGE_KEY,
      JSON.stringify({
        queue: dedupeQueue(queue),
        index: clampIndex(index, Array.isArray(queue) ? queue.length : 0),
      })
    );
  } catch {
    // ignore storage errors
  }
}

function readGlobalVisibilityPreference() {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(GLOBAL_VISIBILITY_STORAGE_KEY);
    if (raw === null || raw === undefined || raw === "") return false;
    return String(raw) === "1";
  } catch {
    return false;
  }
}

function writeGlobalVisibilityPreference(enabled) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(GLOBAL_VISIBILITY_STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    // ignore storage errors
  }
}

export function PodcastPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [track, setTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.8);
  const [playbackRate, setPlaybackRateState] = useState(1);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(-1);
  const [showAcrossApp, setShowAcrossApp] = useState(() => readGlobalVisibilityPreference());

  const trackRef = useRef(null);
  const queueRef = useRef([]);
  const queueIndexRef = useRef(-1);
  const playQueueIndexRef = useRef(async () => false);
  const refreshAegisStreamRef = useRef(async () => false);
  const queueHydratedRef = useRef(false);
  const pendingPlaybackRequestRef = useRef(0);
  const aegisRefreshTimerRef = useRef(null);
  const aegisErrorRecoveryInFlightRef = useRef(false);
  const aegisErrorRecoveryKeyRef = useRef("");

  useEffect(() => {
    queueRef.current = Array.isArray(queue) ? queue : [];
    queueIndexRef.current = Number.isFinite(queueIndex) ? queueIndex : -1;
  }, [queue, queueIndex]);

  useEffect(() => {
    trackRef.current = track;
  }, [track]);

  const clearAegisRefreshTimer = useCallback(() => {
    if (!aegisRefreshTimerRef.current) return;
    if (typeof window !== "undefined") {
      window.clearTimeout(aegisRefreshTimerRef.current);
    } else {
      clearTimeout(aegisRefreshTimerRef.current);
    }
    aegisRefreshTimerRef.current = null;
  }, []);

  const scheduleAegisRefresh = useCallback((nextTrack) => {
    if (typeof window === "undefined") return;
    clearAegisRefreshTimer();
    const prepared = normalizeTrack(nextTrack);
    if (!prepared.isAegis) return;
    const episodeId = normalizeEpisodeId(prepared.episodeId);
    if (!episodeId || !prepared.audioUrl) return;

    let delaySec = 0;
    if (prepared.refreshAfterSec > 0) {
      delaySec = prepared.refreshAfterSec;
    } else {
      const expiresAtMs = parseTimestampToMs(prepared.streamExpiresAt);
      if (!expiresAtMs) return;
      delaySec = Math.max(
        AEGIS_MIN_REFRESH_DELAY_SEC,
        Math.floor((expiresAtMs - Date.now()) / 1000) - AEGIS_REFRESH_SKEW_SEC
      );
    }

    aegisRefreshTimerRef.current = window.setTimeout(() => {
      const active = normalizeTrack(trackRef.current || {});
      if (normalizeEpisodeId(active.episodeId) !== episodeId) return;
      void refreshAegisStreamRef.current(episodeId, false);
    }, Math.max(1000, delaySec * 1000));
  }, [clearAegisRefreshTimer]);

  const applyStreamToTrack = useCallback((baseTrack, stream, fallbackMeta = {}) => {
    const normalizedBase = normalizeTrack(baseTrack);
    const normalizedStream = normalizeAegisStream(stream || {});
    if (!normalizedStream?.manifestUrl) return normalizedBase;
    return normalizeTrack({
      ...normalizedBase,
      ...fallbackMeta,
      isAegis: true,
      audioUrl: normalizedStream.manifestUrl,
      dashManifestUrl: normalizedStream.dashManifestUrl || normalizedBase.dashManifestUrl,
      assetId: normalizedStream.assetId || normalizedBase.assetId,
      projectId: normalizedStream.projectId || normalizedBase.projectId,
      sessionId: normalizedStream.sessionId || normalizedBase.sessionId,
      playbackToken: normalizedStream.playbackToken || normalizedBase.playbackToken,
      streamExpiresAt: normalizedStream.expiresAt || "",
      refreshAfterSec: normalizedStream.refreshAfterSec || 0,
    });
  }, []);

  const refreshAegisStreamByEpisodeId = useCallback(async (episodeId, forcePlay = false) => {
    const resolvedEpisodeId = normalizeEpisodeId(episodeId);
    if (!resolvedEpisodeId) return false;
    const current = normalizeTrack(trackRef.current || {});
    if (normalizeEpisodeId(current.episodeId) !== resolvedEpisodeId) return false;

    let refreshed;
    try {
      refreshed = await playerApi.refreshSession({ episodeId: resolvedEpisodeId });
    } catch {
      if (!current.assetId) return false;
      try {
        refreshed = await playerApi.openPlayback({
          assetId: current.assetId,
          projectId: current.projectId,
          episodeId: resolvedEpisodeId,
          title: current.title,
          posterUrl: current.coverImageUrl,
          durationSeconds: current.durationSeconds,
          ...currentPlaybackUser(),
        });
      } catch {
        return false;
      }
    }

    const parsedOpen = normalizeOpenPlaybackPayload(refreshed || {});
    const stream = parsedOpen.stream || normalizeAegisStream(
      (refreshed && typeof refreshed === "object" ? refreshed.stream : null) || refreshed
    );
    if (!stream?.manifestUrl) return false;

    const audio = audioRef.current;
    if (!audio) return false;

    const beforeTime = Math.max(0, Number(audio.currentTime) || 0);
    const wasPlaying = forcePlay || !audio.paused;
    const nextTrack = applyStreamToTrack(current, stream, {});
    setTrack(nextTrack);

    if (String(audio.src || "") !== String(nextTrack.audioUrl || "")) {
      aegisErrorRecoveryKeyRef.current = "";
      audio.src = nextTrack.audioUrl;
      audio.load();
    }

    try {
      audio.currentTime = beforeTime;
      setCurrentTime(beforeTime);
    } catch {
      // ignore
    }

    if (wasPlaying) {
      setIsLoading(true);
      try {
        await audio.play();
      } catch {
        setIsLoading(false);
      }
    }

    scheduleAegisRefresh(nextTrack);
    return true;
  }, [applyStreamToTrack, scheduleAegisRefresh]);

  useEffect(() => {
    refreshAegisStreamRef.current = refreshAegisStreamByEpisodeId;
  }, [refreshAegisStreamByEpisodeId]);

  const resolveTrackPlaybackSource = useCallback(async (input, options = {}) => {
    const normalized = normalizeTrack(input);
    if (normalized.audioUrl) return normalized;

    const episodeId = normalizeEpisodeId(normalized.episodeId);

    if (episodeId) {
      try {
        const bootstrapQueue = buildBootstrapQueueEpisodeIds(
          episodeId,
          Array.isArray(options.queue) ? options.queue : queueRef.current
        );
        const bootstrap = await playerApi.bootstrapPlayer({
          episodeId,
          queue: bootstrapQueue,
          positionSec: Number.isFinite(Number(options.seekTo)) ? Number(options.seekTo) : 0,
          locale: toSafeLocale(),
        });
        const parsed = normalizeBootstrapPayload(bootstrap || {});
        if (parsed.stream?.manifestUrl) {
          return applyStreamToTrack(normalized, parsed.stream, {
            episodeId: parsed.episodeId || normalized.episodeId,
            title: parsed.title || normalized.title,
            podcastTitle: parsed.author || normalized.podcastTitle,
            coverImageUrl: parsed.posterUrl || normalized.coverImageUrl,
            durationSeconds: parsed.durationSeconds || normalized.durationSeconds,
          });
        }
      } catch {
        // fallback below through /api/player/open when the local bootstrap path is unavailable
      }
    }

    if (normalized.assetId) {
      try {
        const opened = await playerApi.openPlayback({
          assetId: normalized.assetId,
          projectId: normalized.projectId,
          episodeId,
          title: normalized.title,
          posterUrl: normalized.coverImageUrl,
          durationSeconds: normalized.durationSeconds,
          ...currentPlaybackUser(),
        });
        const parsed = normalizeOpenPlaybackPayload(opened || {});
        if (parsed.stream?.manifestUrl) {
          return applyStreamToTrack(normalized, parsed.stream, {
            episodeId: parsed.episodeId || normalized.episodeId,
            assetId: parsed.assetId || normalized.assetId,
            projectId: parsed.projectId || normalized.projectId,
            title: parsed.title || normalized.title,
            coverImageUrl: parsed.posterUrl || normalized.coverImageUrl,
            durationSeconds: parsed.durationSeconds || normalized.durationSeconds,
          });
        }
      } catch {
        return normalized;
      }
    }

    return normalized;
  }, [applyStreamToTrack]);

  const startTrackPlayback = useCallback(async (input, options = {}) => {
    const requestId = pendingPlaybackRequestRef.current + 1;
    pendingPlaybackRequestRef.current = requestId;

    const audio = audioRef.current;
    if (!audio) return false;

    const resolvedTrack = await resolveTrackPlaybackSource(input, options);
    if (pendingPlaybackRequestRef.current !== requestId) return false;

    const nextTrack = normalizeTrack(resolvedTrack);
    setTrack(nextTrack);
    setVisible(true);
    setError("");

    if (!nextTrack.audioUrl) {
      setIsPlaying(false);
      setIsLoading(false);
      setError(
        nextTrack.isAegis || nextTrack.embedUrl
          ? "Не удалось получить поток Aegis для этого эпизода."
          : "У эпизода пока нет доступного аудио"
      );
      return false;
    }

    const mustReplaceSrc = String(audio.src || "") !== String(nextTrack.audioUrl || "");
    if (mustReplaceSrc) {
      aegisErrorRecoveryKeyRef.current = "";
      setCurrentTime(0);
      setDuration(toSafeNumber(nextTrack.durationSeconds, 0));
      audio.src = nextTrack.audioUrl;
      audio.load();
    }

    if (Number.isFinite(Number(options.seekTo)) && Number(options.seekTo) >= 0) {
      const seekTo = Number(options.seekTo);
      try {
        audio.currentTime = seekTo;
        setCurrentTime(seekTo);
      } catch {
        // ignore seek errors before metadata
      }
    }

    scheduleAegisRefresh(nextTrack);

    const autoplay = options.autoplay !== false;
    if (!autoplay) {
      setIsLoading(false);
      return true;
    }

    setIsLoading(true);
    try {
      await audio.play();
      return true;
    } catch {
      setIsLoading(false);
      setIsPlaying(false);
      setError("Браузер заблокировал автозапуск. Нажмите Play.");
      return true;
    }
  }, [resolveTrackPlaybackSource, scheduleAegisRefresh]);

  const playQueueIndex = useCallback(
    async (index, options = {}) => {
      const currentQueue = Array.isArray(queueRef.current) ? queueRef.current : [];
      if (!currentQueue.length) return false;
      const safeIndex = clampIndex(index, currentQueue.length);
      if (safeIndex < 0) return false;
      const nextTrack = currentQueue[safeIndex];
      setQueueIndex(safeIndex);
      return startTrackPlayback(nextTrack, options);
    },
    [startTrackPlayback]
  );

  useEffect(() => {
    playQueueIndexRef.current = playQueueIndex;
  }, [playQueueIndex]);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = "metadata";
    audio.volume = 0.8;
    audio.playbackRate = 1;
    audioRef.current = audio;

    const onPlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
    };
    const onPause = () => setIsPlaying(false);
    const onLoadedMetadata = () => {
      const metaDuration = Number(audio.duration);
      setDuration(Number.isFinite(metaDuration) ? metaDuration : 0);
      setIsLoading(false);
    };
    const onDurationChange = () => {
      const next = Number(audio.duration);
      if (Number.isFinite(next)) setDuration(next);
    };
    const onTimeUpdate = () => {
      const next = Number(audio.currentTime);
      if (Number.isFinite(next)) setCurrentTime(next);
    };
    const onEnded = () => {
      setIsPlaying(false);
      const nextIndex = queueIndexRef.current + 1;
      if (nextIndex < (queueRef.current?.length || 0)) {
        void playQueueIndexRef.current(nextIndex, { autoplay: true });
      }
    };
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);
    const onError = () => {
      const active = normalizeTrack(trackRef.current || {});
      const activeEpisodeId = normalizeEpisodeId(active.episodeId);
      const recoveryKey = `${activeEpisodeId}:${active.audioUrl || ""}`;
      if (
        active.isAegis &&
        activeEpisodeId &&
        recoveryKey &&
        aegisErrorRecoveryKeyRef.current !== recoveryKey &&
        !aegisErrorRecoveryInFlightRef.current
      ) {
        aegisErrorRecoveryKeyRef.current = recoveryKey;
        aegisErrorRecoveryInFlightRef.current = true;
        setIsLoading(true);
        setError("Обновляем сессию Aegis…");
        void refreshAegisStreamRef.current(activeEpisodeId, true)
          .then((ok) => {
            if (!ok) {
              setIsLoading(false);
              setIsPlaying(false);
              setError("Не удалось запустить поток Aegis");
            } else {
              setError("");
            }
          })
          .finally(() => {
            aegisErrorRecoveryInFlightRef.current = false;
          });
        return;
      }
      setIsLoading(false);
      setIsPlaying(false);
      setError("Не удалось запустить аудио эпизода");
    };

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("error", onError);

    return () => {
      clearAegisRefreshTimer();
      audio.pause();
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("error", onError);
      audioRef.current = null;
    };
  }, [clearAegisRefreshTimer]);

  useEffect(() => {
    const restored = readStoredQueue();
    setQueue(restored.queue);
    setQueueIndex(restored.index);
    if (readGlobalVisibilityPreference() && restored.index >= 0 && restored.queue[restored.index]) {
      void startTrackPlayback(restored.queue[restored.index], { autoplay: false });
    }
    queueHydratedRef.current = true;
  }, [startTrackPlayback]);

  useEffect(() => {
    if (!queueHydratedRef.current) return;
    writeStoredQueue(queue, queueIndex);
  }, [queue, queueIndex]);

  useEffect(() => {
    writeGlobalVisibilityPreference(Boolean(showAcrossApp));
  }, [showAcrossApp]);

  const playTrack = useCallback(
    async (input, options = {}) => {
      const nextTrack = normalizeTrack(input);
      const key = trackKey(nextTrack);
      const hasResolvableSource = canResolveTrack(nextTrack);

      if (Array.isArray(options.queue)) {
        const normalizedQueue = dedupeQueue(options.queue);
        let nextIndex = clampIndex(options.queueStartIndex, normalizedQueue.length);
        if (nextIndex < 0) {
          nextIndex = normalizedQueue.findIndex((item) => trackKey(item) === key);
        }
        if (nextIndex < 0 && hasResolvableSource) {
          normalizedQueue.push(nextTrack);
          nextIndex = normalizedQueue.length - 1;
        }
        setQueue(normalizedQueue);
        setQueueIndex(nextIndex);
      } else {
        const currentQueue = Array.isArray(queueRef.current) ? queueRef.current : [];
        const existingIndex = currentQueue.findIndex((item) => trackKey(item) === key);
        if (existingIndex >= 0) {
          setQueueIndex(existingIndex);
        } else if (hasResolvableSource) {
          const nextQueue = [...currentQueue, nextTrack];
          setQueue(nextQueue);
          setQueueIndex(nextQueue.length - 1);
        }
      }

      return startTrackPlayback(nextTrack, options);
    },
    [startTrackPlayback]
  );

  const enqueueTrack = useCallback((input) => {
    const nextTrack = normalizeTrack(input);
    if (!canResolveTrack(nextTrack)) return false;
    const key = trackKey(nextTrack);
    const currentQueue = Array.isArray(queueRef.current) ? queueRef.current : [];
    if (currentQueue.some((item) => trackKey(item) === key)) return false;
    setQueue([...currentQueue, nextTrack]);
    return true;
  }, []);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!audio.src) {
      if (queueIndexRef.current >= 0) {
        await playQueueIndexRef.current(queueIndexRef.current, { autoplay: true });
      }
      return;
    }

    if (audio.paused) {
      setIsLoading(true);
      try {
        await audio.play();
      } catch {
        setIsLoading(false);
        setError("Не удалось продолжить воспроизведение");
      }
      return;
    }

    audio.pause();
  }, []);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
  }, []);

  const seekTo = useCallback((seconds) => {
    const audio = audioRef.current;
    if (!audio) return;
    const safe = Math.max(0, Number(seconds) || 0);
    const max = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : safe;
    const next = Math.min(safe, max);
    try {
      audio.currentTime = next;
      setCurrentTime(next);
    } catch {
      // ignore
    }
  }, []);

  const seekBy = useCallback((deltaSeconds) => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = (Number(audio.currentTime) || 0) + (Number(deltaSeconds) || 0);
    const max = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : next;
    const bounded = Math.max(0, Math.min(next, max));
    try {
      audio.currentTime = bounded;
      setCurrentTime(bounded);
    } catch {
      // ignore
    }
  }, []);

  const setVolume = useCallback((nextVolume) => {
    const audio = audioRef.current;
    const safe = Math.max(0, Math.min(1, Number(nextVolume) || 0));
    setVolumeState(safe);
    if (audio) audio.volume = safe;
  }, []);

  const setPlaybackRate = useCallback((nextRate) => {
    const audio = audioRef.current;
    const safe = [0.75, 1, 1.25, 1.5, 2].includes(Number(nextRate)) ? Number(nextRate) : 1;
    setPlaybackRateState(safe);
    if (audio) audio.playbackRate = safe;
  }, []);

  const playNextTrack = useCallback(async () => {
    const nextIndex = queueIndexRef.current + 1;
    if (nextIndex >= (queueRef.current?.length || 0)) return false;
    return playQueueIndex(nextIndex, { autoplay: true });
  }, [playQueueIndex]);

  const playPrevTrack = useCallback(async () => {
    const prevIndex = queueIndexRef.current - 1;
    if (prevIndex < 0) return false;
    return playQueueIndex(prevIndex, { autoplay: true });
  }, [playQueueIndex]);

  const removeFromQueue = useCallback((indexToRemove) => {
    const currentQueue = Array.isArray(queueRef.current) ? queueRef.current : [];
    if (!currentQueue.length) return;
    const safeIndex = clampIndex(indexToRemove, currentQueue.length);
    if (safeIndex < 0) return;

    const nextQueue = currentQueue.filter((_, idx) => idx !== safeIndex);
    const currentIdx = queueIndexRef.current;

    if (!nextQueue.length) {
      setQueue([]);
      setQueueIndex(-1);
      return;
    }

    if (safeIndex === currentIdx) {
      const fallbackIndex = Math.min(currentIdx, nextQueue.length - 1);
      setQueue(nextQueue);
      setQueueIndex(fallbackIndex);
      void startTrackPlayback(nextQueue[fallbackIndex], { autoplay: isPlaying });
      return;
    }

    const adjustedIndex = safeIndex < currentIdx ? currentIdx - 1 : currentIdx;
    setQueue(nextQueue);
    setQueueIndex(clampIndex(adjustedIndex, nextQueue.length));
  }, [isPlaying, startTrackPlayback]);

  const clearQueue = useCallback(() => {
    setQueue([]);
    setQueueIndex(-1);
  }, []);

  const closePlayer = useCallback(() => {
    clearAegisRefreshTimer();
    aegisErrorRecoveryInFlightRef.current = false;
    aegisErrorRecoveryKeyRef.current = "";
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    setVisible(false);
    setTrack(null);
    setIsPlaying(false);
    setIsLoading(false);
    setCurrentTime(0);
    setDuration(0);
    setError("");
  }, [clearAegisRefreshTimer]);

  const clearError = useCallback(() => setError(""), []);

  const canPlayPrev = queueIndex > 0;
  const canPlayNext = queueIndex >= 0 && queueIndex < queue.length - 1;

  const value = useMemo(
    () => ({
      visible,
      track,
      isPlaying,
      isLoading,
      error,
      currentTime,
      duration,
      volume,
      playbackRate,
      queue,
      queueIndex,
      showAcrossApp,
      canPlayPrev,
      canPlayNext,
      playTrack,
      enqueueTrack,
      addToQueue: enqueueTrack,
      playAt: playQueueIndex,
      playQueueIndex,
      playNextTrack,
      playPrevTrack,
      removeFromQueue,
      clearQueue,
      setShowAcrossApp,
      togglePlay,
      pause,
      seekTo,
      seekBy,
      setVolume,
      setPlaybackRate,
      closePlayer,
      clearError,
    }),
    [
      visible,
      track,
      isPlaying,
      isLoading,
      error,
      currentTime,
      duration,
      volume,
      playbackRate,
      queue,
      queueIndex,
      showAcrossApp,
      canPlayPrev,
      canPlayNext,
      playTrack,
      enqueueTrack,
      playQueueIndex,
      playNextTrack,
      playPrevTrack,
      removeFromQueue,
      clearQueue,
      setShowAcrossApp,
      togglePlay,
      pause,
      seekTo,
      seekBy,
      setVolume,
      setPlaybackRate,
      closePlayer,
      clearError,
    ]
  );

  return <PodcastPlayerContext.Provider value={value}>{children}</PodcastPlayerContext.Provider>;
}

export function usePodcastPlayer() {
  const value = useContext(PodcastPlayerContext);
  if (!value) {
    throw new Error("usePodcastPlayer must be used inside PodcastPlayerProvider");
  }
  return value;
}
